import useGradualIncrement from '../../../../../hooks/useGradualIncrement';
import LangEnum from '../../../../../lang/LangEnum';
import OfflineModalProps from '../../../../../modules/offline/components/OfflineModal/offlineModal.types';
import { useCanUseOfflineMode } from '../../../../../modules/offline/hooks/useCanUseOfflineMode';
import { useOfflineDataDownloadInformation } from '../../../../../modules/offline/hooks/useOfflineDataDownloadInformation';
import { useOfflineDownloaderWithModal } from '../../../../../modules/offline/hooks/useOfflineDownloaderWithModal/useOfflineDownloaderWithModal';
import { useTranslation } from '../../../../../modules/translation/TranslationContext';
import { useGetOfflineEncryptionKey } from '../../../../../services/modules/offline/offline.queries';
import { UserMenuOfflineItemProps } from '../userMenu.types';

interface UseOfflineMenuItemReturn {
  itemProps: Omit<UserMenuOfflineItemProps, 'onClick'>;
  openOfflineModal: () => void;
  offlineModalProps: OfflineModalProps;
}

const SYNCED_PERCENTAGE_TRANSITION_MS = 1500;

export const useOfflineMenuItem = (): UseOfflineMenuItemReturn => {
  const { translate } = useTranslation();
  const canUseOffline = useCanUseOfflineMode();

  const {
    isSyncing,
    lastSyncedAt,
    hasSyncedData,
    isOpenOfflineModal,
    openOfflineModal,
    closeOfflineModal,
    updateOfflineData,
    handleRemoveOfflineData,
    handleDownloadOfflineData,
  } = useOfflineDownloaderWithModal();

  const { totalSyncedPercentage, hasSyncedData: hasFullySyncedData } =
    useOfflineDataDownloadInformation();
  const syncedPercentage = useGradualIncrement(
    totalSyncedPercentage,
    SYNCED_PERCENTAGE_TRANSITION_MS,
  );

  const { data: offlineEncryptionKey } = useGetOfflineEncryptionKey({
    options: { enabled: false },
  });

  const isLoadingEncryptionKey = hasFullySyncedData && !offlineEncryptionKey;

  return {
    itemProps: {
      show: canUseOffline,
      isSyncing,
      isDisabled: isSyncing || isLoadingEncryptionKey,
      label: translate(LangEnum.GLOBAL_OFFLINE_MODE),
      tooltipContent: isLoadingEncryptionKey ? translate(LangEnum.OFFLINE_LOADING_KEY_MESSAGE) : '',
      secondaryLabel: isSyncing
        ? `${translate(LangEnum.GLOBAL_DOWNLOADING_DATA)} (${syncedPercentage}%)`
        : undefined,
    },
    openOfflineModal,
    offlineModalProps: {
      open: openOfflineModal,
      isOpen: isOpenOfflineModal,
      onClose: closeOfflineModal,
      lastSyncedAt,
      hasSyncedData,
      updateData: updateOfflineData,
      downloadData: handleDownloadOfflineData,
      removeOfflineData: handleRemoveOfflineData,
    },
  };
};
